import { useState } from 'react'
import {
  ClipboardList,
  LayoutGrid,
  PenSquare,
  ShieldCheck,
  MessageSquare,
  Eye,
  ImageIcon,
  ArrowRight,
  Play,
  Share2,
  Search,
} from 'lucide-react'
import { VideoModal } from '../components/VideoGuidePanel'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export function OverviewTab() {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {showVideo && (
        <VideoModal
          src="/api/static/content-tracker/about-videos/Overview_General.mp4"
          title="Overview — General"
          onClose={() => setShowVideo(false)}
        />
      )}

      {/* Intro */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Introduction</p>
        </div>
        <CardHeader className="pb-0">
          <CardTitle className="text-xl">What is Content Tracker?</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="flex gap-8 items-stretch">
            <div className="flex-1 min-w-0 flex flex-col gap-6">
              <p className="text-base text-slate-600 leading-relaxed">
                Content Tracker is the{' '}
                <span className="font-semibold text-slate-900">single source of truth</span>{' '}
                for all digital campaign creative work within Ford EU18 markets.
                It connects every stakeholder — from media planning through to campaign launch — in one unified system.
              </p>

              <div className="border-l-[3px] border-[#0014cc] pl-4 py-0.5">
                <p className="text-sm text-[#0014cc] leading-relaxed">
                  Content Tracker replaces all parallel tracking documents. No spreadsheets, no local copies — all stakeholders must work within Content Tracker.
                  Notifications are sent automatically via Microsoft Teams when statuses change.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowVideo(true)}
              className="w-72 flex-shrink-0 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden group cursor-pointer flex flex-col"
            >
              <div className="relative flex-1">
                <video
                  src="/api/static/content-tracker/about-videos/Overview_General.mp4"
                  muted
                  preload="metadata"
                  className="w-full h-full object-cover"
                  onLoadedData={(e) => { (e.target as HTMLVideoElement).currentTime = 0.5; }}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm">
                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 text-left flex-shrink-0">
                <p className="text-sm font-medium text-slate-700">Overview — General</p>
                <p className="text-xs text-slate-400 mt-0.5">Full walkthrough of Content Tracker</p>
              </div>
            </button>
          </div>

          <div className="space-y-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              End-to-end workflow
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { label: 'MAP', color: 'bg-blue-50 text-blue-700 border-blue-100' },
                { label: 'Creative Agency', color: 'bg-purple-50 text-purple-700 border-purple-100' },
                { label: 'Activation', color: 'bg-green-50 text-green-700 border-green-100' },
                { label: 'Client Approval', color: 'bg-orange-50 text-orange-700 border-orange-100' },
                { label: 'Live Campaign', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-2">
                  <span className={`border px-3 py-1 rounded-full text-sm font-medium ${step.color}`}>
                    {step.label}
                  </span>
                  {i < arr.length - 1 && (
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Navigate */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Areas</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">How to navigate the system</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                icon: ClipboardList,
                badge: 'TAB',
                title: 'Plan Approval',
                desc: 'Used by Planners to review imported plans, resolve errors, approve plans, and trigger row generation.',
                channels: ['Social', 'Search'] as const,
              },
              {
                icon: LayoutGrid,
                badge: 'TAB',
                title: 'Content Tracker',
                desc: 'Used by all stakeholders to view rows, monitor status, add preview links, and update live status.',
                channels: ['Social', 'Search'] as const,
              },
              {
                icon: PenSquare,
                badge: 'OVERLAY',
                title: 'Content Modal',
                desc: 'The creative production engine. Used to upload assets, enter copy, run AI validation, and approve bundles.',
                channels: ['Social', 'Search'] as const,
              },
              {
                icon: ShieldCheck,
                badge: 'OVERLAY',
                title: 'Status Modal',
                desc: 'The governance and approval engine. Used for lifecycle tracking, SLA monitoring, comments, and preview approval.',
                channels: ['Social', 'Search'] as const,
              },
            ].map((item) => {
              const Icon = item.icon
              return (
                <Card key={item.title} className="shadow-none bg-slate-50/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#0014cc]" strokeWidth={1.75} />
                      </div>
                      <div className="flex flex-col justify-center h-11 min-w-0 flex-1">
                        <span className="text-[11px] font-bold tracking-widest uppercase text-[#0014cc]">
                          {item.badge}
                        </span>
                        <CardTitle className="text-sm mt-0.5">{item.title}</CardTitle>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {item.channels.map((ch) => (
                          <span
                            key={ch}
                            className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-slate-200 bg-white text-[#0014cc]"
                          >
                            {ch === 'Social' ? <Share2 className="w-2.5 h-2.5" /> : <Search className="w-2.5 h-2.5" />}
                            {ch}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="leading-relaxed">{item.desc}</CardDescription>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Status & Ownership */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Lifecycle</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Status & ownership model</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            Every row has a status that determines ownership and permissions. Only the current owner should progress a row.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="border-t border-slate-100 overflow-hidden rounded-b-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Owner</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Notification</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Waiting for Client Brief', 'Creative Agency', 'Creative Agency notified'],
                  ['Asset in Production', 'Creative Agency', 'Creative Agency notified'],
                  ['Ad Copy Creation', 'Creative Agency*', 'Creative Agency notified*'],
                  ['Activation in Setup', 'Activation Team', 'Activation Team notified'],
                  ['Waiting for Preview Approval', 'Approver (Local Ford)', 'Client/Approver notified'],
                  ['Preview Approved', 'Activation Team', 'Activation Team notified'],
                  ['Campaign Live', 'Activation Team', 'Market-wide notification'],
                ].map(([status, owner, notification], i) => (
                  <tr key={status} className={`border-b border-slate-100 last:border-0 ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                    <td className="px-6 py-4 font-medium text-slate-800">{status}</td>
                    <td className="px-6 py-4 text-slate-500">{owner}</td>
                    <td className="px-6 py-4 text-slate-500">{notification}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="px-6 py-3 text-xs text-slate-400 italic">
                    * For the ESO markets, Local Ford Teams will add the Ad Copy and will be notified via email instead.
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Comments & Archive */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Communication</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Comments & archive</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { icon: ImageIcon,    title: 'Asset-Level Comments', desc: 'Located in Asset Overlay. Used for creative feedback between Creative Agency and Activation.',  iconBg: 'bg-slate-100', iconColor: 'text-[#0014cc]' },
              { icon: MessageSquare, title: 'General Comments',    desc: 'Located in Status Overlay. Used for process-level updates, delays, or production issues.',      iconBg: 'bg-slate-100', iconColor: 'text-[#0014cc]' },
              { icon: Eye,          title: 'Preview Comments',     desc: 'Located in Status Overlay. Used for client review feedback and approval confirmation.',          iconBg: 'bg-slate-100', iconColor: 'text-[#0014cc]' },
            ].map((item) => {
              const Icon = item.icon
              return (
                <Card key={item.title} className="shadow-none bg-slate-50/50">
                  <CardHeader className="pb-3">
                    <div className={`w-9 h-9 rounded-lg ${item.iconBg} flex items-center justify-center mb-1`}>
                      <Icon className={`w-4 h-4 ${item.iconColor}`} strokeWidth={1.75} />
                    </div>
                    <CardTitle className="text-sm">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="leading-relaxed">{item.desc}</CardDescription>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <div className="border-l-[3px] border-slate-300 pl-5">
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-800">Archive</span> is a notification management tool — not deletion. Archived rows are greyed out, hidden from default view, but all data is preserved and can be unarchived at any time.
            </p>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}
